import { AbsoluteCenter, Box, EmptyState, HStack, Icon, IconButton, Table, VStack } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  type GroupingState,
  type Row,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GoGraph } from 'react-icons/go';
import { IoIosInformationCircleOutline } from 'react-icons/io';
import { MdOutlineExpandMore } from 'react-icons/md';

import { type IndicatorPublic, IndicatorsService } from '@/client/index.ts';
import Loader from '@/components/Loader.tsx';
import { CTag } from '@/components/common/CTag.tsx';
import { Pagination } from '@/components/common/Pagination.tsx';
import { ResultNotFound } from '@/components/common/ResultNotFound.tsx';
import IndicatorDrawer from '@/components/indicators/index/drawer/IndicatorDrawer.tsx';
import { Route } from '@/routes/_layout/$slug/indicators/index.tsx';

import { columnsDef, defaultColumnVisibility } from './IndicatorData.tsx';
import IndicatorSummaryDrawer from './drawer/IndicatorSummaryDrawer.tsx';

function getIndicatorsQueryOptions({
  slug,
  page,
  pageSize,
  filter,
  sort,
  search,
  group_by,
}: {
  slug: string;
  page: number;
  pageSize: number;
  filter: string;
  sort: string;
  search: string;
  group_by: string;
}) {
  return {
    queryKey: ['indicators', { page, pageSize, filter, sort, search }],
    queryFn: async () => {
      return await IndicatorsService.readIndicators({
        offset: group_by !== '' ? 1 : (page - 1) * pageSize,
        limit: group_by !== '' ? 9999 : pageSize,
        companySlug: slug,
        filter: filter,
        sort: sort,
        search: search,
      });
    },
  };
}

export function IndicatorTable() {
  const navigate = useNavigate({ from: Route.fullPath });
  const { t } = useTranslation();
  const { page, pageSize, filter, sort, columns, search, group_by } = Route.useSearch();
  const { slug } = useParams({ strict: false });
  const [count, setCount] = useState<number>(0);
  const [isIndicatorDrawerOpen, setIsIndicatorDrawerOpen] = useState(false);
  const [indicatorSummaryId, setIndicatorSummaryId] = useState<string | null>(null);
  const [selectedIndicator, setSelectedIndicator] = useState<IndicatorPublic | undefined>();
  const [grouping, setGrouping] = useState<GroupingState>([]);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const {
    data: indicatorsData,
    isLoading,
    refetch,
  } = useQuery({
    ...getIndicatorsQueryOptions({
      slug,
      page,
      pageSize,
      filter,
      sort,
      search,
      group_by,
    }),
  });

  useEffect(() => {
    if (indicatorsData && indicatorsData.count !== undefined) {
      setCount(indicatorsData.count);
    }
  }, [indicatorsData]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (grouping.length === 0 && group_by !== '') setGrouping([group_by]);
    else if (grouping.length > 0 && grouping[0] !== group_by) {
      setGrouping([group_by]);
    }
    refetch();
  }, [page, filter, sort, search, pageSize, group_by]);

  useEffect(() => {
    if (indicatorsData && count < page * pageSize && page > 1) {
      navigate({
        search: (prev: Record<string, string>) => ({
          ...prev,
          page: Math.floor(count / pageSize) + 1,
        }),
      });
    }
  });

  const handleSelectedIndicator = (row: Row<IndicatorPublic>) => {
    if (row.getIsGrouped()) {
      row.toggleExpanded();
    } else {
      setSelectedIndicator(row.original);
      setIsIndicatorDrawerOpen(true);
    }
  };

  const parsedColumns = useMemo(() => {
    if (columns === '') return defaultColumnVisibility;

    const visibleColumns = new Set(columns.split(','));
    visibleColumns.add(group_by); // Ensure group_by is included

    return columnsDef.reduce<Record<string, boolean>>((acc, column) => {
      acc[column.accessorKey] = visibleColumns.has(column.accessorKey);
      return acc;
    }, {});
  }, [columns, group_by]);

  const table = useReactTable({
    data: indicatorsData?.data || [],
    columns: columnsDef,
    initialState: {
      columnVisibility: defaultColumnVisibility,
      grouping: [],
    },
    state: {
      columnVisibility: parsedColumns,
      grouping: grouping,
    },
    manualSorting: true,
    manualFiltering: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  const isPaginated =
    count !== 0 && count > pageSize && indicatorsData?.data.length !== undefined && indicatorsData?.data.length < count;

  return (
    <>
      {isLoading ? (
        <Loader loadingText={t('loading.indicators')} />
      ) : (
        <>
          {count > 0 && (
            <>
              <Box maxH={'calc(95vh - 176px)'} borderColor={'transparent'}>
                <IndicatorDrawer
                  indicatorId={selectedIndicator?.id ?? undefined}
                  setIsOpen={setIsIndicatorDrawerOpen}
                  isOpen={isIndicatorDrawerOpen}
                  refetch={refetch}
                />
                <Table.ScrollArea
                  maxH={'calc(95vh - 176px)'}
                  whiteSpace={'break-spaces'}
                  borderWidth={1}
                  ref={scrollAreaRef}
                  borderColor={'transparent'}
                  w={'100%'}
                >
                  <Table.Root size="md" interactive stickyHeader w={'100%'}>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <Table.Header key={headerGroup.id} borderColor={'transparent'} w={'100%'}>
                        <Table.Row bg={'#F1F7FA'} w={'100%'}>
                          {headerGroup.headers.map((header) => (
                            <Table.ColumnHeader
                              key={header.id}
                              style={{ width: `${header.getSize()}px` }}
                              alignContent={'end'}
                              placeContent={'center'}
                              p={4}
                            >
                              <Box display={'flex'} flexDir={'column'}>
                                <Box
                                  color={{ base: 'gray.600', _dark: 'white' }}
                                  fontWeight={'semibold'}
                                  display={'flex'}
                                >
                                  {flexRender(header.column.columnDef.header, header.getContext())}
                                </Box>
                              </Box>
                            </Table.ColumnHeader>
                          ))}
                        </Table.Row>
                      </Table.Header>
                    ))}
                    <Table.Body>
                      {table.getRowModel().rows.map((row) => (
                        <Table.Row key={row.id} cursor={'pointer'} onClick={() => handleSelectedIndicator(row)}>
                          {row.getVisibleCells().map((cell) => (
                            <Table.Cell key={cell.id} p={4}>
                              {cell.getIsGrouped() ? (
                                <Box>
                                  <HStack>
                                    <Icon
                                      size={'md'}
                                      rotate={row.getIsExpanded() ? '0deg' : '-90deg'}
                                      transition={'rotate 0.2s ease-in-out'}
                                    >
                                      <MdOutlineExpandMore />
                                    </Icon>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}{' '}
                                    <CTag
                                      label={`${row.subRows.length}`}
                                      color={'primary.50'}
                                      bg={'primary.600'}
                                      ml={'auto'}
                                    />
                                  </HStack>
                                </Box>
                              ) : cell.getIsAggregated() ? (
                                flexRender(
                                  cell.column.columnDef.aggregatedCell ?? cell.column.columnDef.cell,
                                  cell.getContext()
                                )
                              ) : cell.getIsPlaceholder() ? null : (
                                <HStack>
                                  {'accessorKey' in cell.column.columnDef &&
                                    cell.column.columnDef.accessorKey === 'name' && (
                                      <>
                                        <IconButton
                                          size="xs"
                                          variant={'outline'}
                                          borderRadius={'50%'}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            handleSelectedIndicator(row);
                                          }}
                                        >
                                          <IoIosInformationCircleOutline />
                                        </IconButton>
                                        <IconButton
                                          size="xs"
                                          variant={'outline'}
                                          borderRadius={'50%'}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            setIndicatorSummaryId(row.original.id ?? '');
                                          }}
                                        >
                                          <GoGraph />
                                        </IconButton>
                                      </>
                                    )}
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </HStack>
                              )}
                            </Table.Cell>
                          ))}
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                </Table.ScrollArea>
              </Box>
              <Box>
                <Pagination
                  isPaginated={isPaginated}
                  count={count}
                  page={page}
                  pageSize={pageSize}
                  navigate={navigate}
                />
              </Box>
            </>
          )}
          <IndicatorSummaryDrawer
            indicatorId={indicatorSummaryId ?? undefined}
            isOpen={indicatorSummaryId !== null}
            setIsOpen={(state: boolean) => setIndicatorSummaryId(state ? indicatorSummaryId : null)}
            editDisabled={true}
          />
          {count === 0 && filter !== '' && sort !== '' && <ResultNotFound />}
          {count === 0 && filter === '' && sort === '' && (
            <AbsoluteCenter>
              <EmptyState.Root>
                <EmptyState.Content>
                  <VStack textAlign="center">
                    <EmptyState.Title>{t('messages.hints.addIndicator.title')}</EmptyState.Title>
                    <EmptyState.Description>{t('messages.hints.addFirstIndicator')}</EmptyState.Description>
                  </VStack>
                </EmptyState.Content>
              </EmptyState.Root>
            </AbsoluteCenter>
          )}
        </>
      )}
    </>
  );
}

export default IndicatorTable;
